import React, { useEffect, useState } from "react";
import _ from "lodash";
import { useOutletContext, useNavigate } from "react-router-dom";
import {
  ManageAccounts,
  EditOutlined,
  DeleteForeverRounded,
  LockResetOutlined,
  Search,
} from "@mui/icons-material";

import InfoCard from "components/Card/InfoCard";
import PaginatedTable from "components/PaginatedTable";
import ModuleHeader from "components/Card/ModuleHeader";
import DeleteConfirmation from "components/Card/Confirmation";

import LoadingModal from "components/LoadingModal";
import CreateUserForm from "./form";
import UpdateUserForm from "./form";

import UserManagementHOC from "./actions";
import { getTranslation } from "utils/renderLanguage";

const UserManagement = (props) => {
  const navigate = useNavigate();
  const outletContext = useOutletContext();
  const cardList = [
    {
      label: getTranslation(outletContext.language, "Total User"),
      value: "total_user",
    },
    {
      label: getTranslation(outletContext.language, "Total Admin/Secretariat"),
      value: "total_secretariat",
    },
    {
      label: getTranslation(outletContext.language, "Total Assessor"),
      value: "total_assessor",
    },
    {
      label: getTranslation(outletContext.language, "Total Project Leader"),
      value: "total_leader",
    },
  ];

  const columnData = [
    {
      header: getTranslation(outletContext.language, "Name"),
      accessor: "display_name",
    },
    {
      header: getTranslation(outletContext.language, "Email"),
      accessor: "email",
    },
    {
      header: getTranslation(outletContext.language, "Mobile No"),
      accessor: "mobile_no",
    },
    {
      header: getTranslation(outletContext.language, "Role"),
      accessor: "role",
    },
    {
      header: getTranslation(outletContext.language, "Project"),
      renderColumn: (rowData) =>
        rowData.project
          .map((item) => {
            const project = outletContext.projectDictionary.find(
              (x) => x.value === item
            );
            return project ? project.label : "";
          })
          .join(", "),
    },
    {
      header: getTranslation(outletContext.language, "Status"),
      accessor: "status",
    },
  ];

  const [searchQuery, setSearchQuery] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);

  useEffect(() => {
    props.getUsers();
    outletContext.getUserDictionary();
    outletContext.getProjectDictionary();
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredUsers(props.searchUserList);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = props.searchUserList.filter((user) => {
        const displayName = (user.display_name || "").toLowerCase();
        const email = (user.email || "").toLowerCase();
        return displayName.includes(query) || email.includes(query);
      });
      setFilteredUsers(filtered);
    }
  }, [searchQuery, props.searchUserList]);

  return (
    <>
      <ModuleHeader
        title={getTranslation(outletContext.language, "User Management")}
        description={getTranslation(
          outletContext.language,
          "This module use to manage all the user in the system."
        )}
        actionButton={[
          {
            label: getTranslation(outletContext.language, "Create User"),
            className: "btn btn-primary",
            onClick: () =>
              props.onChangeUserManagementHOC(true, "showCreateModal"),
          },
        ]}
        renderIcon={() => <ManageAccounts />}
      />
      <InfoCard cardList={cardList} dataValue={props.dataValue} />
      <div style={{ margin: "20px 0", display: "flex", alignItems: "center", gap: "10px" }}>
        <div style={{ position: "relative", width: "100%", maxWidth: "400px" }}>
          <Search
            style={{
              position: "absolute",
              left: "12px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "#666",
              fontSize: "20px"
            }}
          />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: "100%",
              padding: "10px 12px 10px 45px",
              fontSize: "14px",
              border: "1px solid #ddd",
              borderRadius: "4px",
              outline: "none"
            }}
            onFocus={(e) => e.target.style.borderColor = "#007bff"}
            onBlur={(e) => e.target.style.borderColor = "#ddd"}
          />
        </div>
      </div>
      <PaginatedTable
        rowData={filteredUsers}
        columnData={columnData}
        actionsColumn={[
          {
            name: "Edit",
            renderActionIcon: <EditOutlined />,
            onClick: (rowData) =>
              Promise.all([
                props.onChangeUserManagementHOC(rowData, "selectedUser"),
              ]).then(() => {
                props.onChangeUserManagementHOC(true, "showEditModal");
              }),
          },
          {
            name: "Reset Password",
            style: { background: "#ff9800" },
            renderActionIcon: <LockResetOutlined />,
            onClick: (rowData) =>
              Promise.all([
                props.onChangeUserManagementHOC(rowData, "selectedUser"),
              ]).then(() => {
                props.onChangeUserManagementHOC(true, "showResetPasswordModal");
              }),
          },
          {
            name: "Delete",
            style: { background: "#cb1515" },
            renderActionIcon: <DeleteForeverRounded />,
            onClick: (rowData) =>
              Promise.all([
                props.onChangeUserManagementHOC(rowData, "selectedUser"),
              ]).then(() => {
                props.onChangeUserManagementHOC(true, "showDeleteModal");
              }),
          },
        ]}
      />
      {props.showCreateModal && (
        <CreateUserForm
          mode={"create"}
          title={getTranslation(outletContext.language, "Create New User")}
          language={outletContext.language}
          createNewUser={props.createNewUser}
          projectDictionary={outletContext.projectDictionary}
          setOpen={(val) =>
            props.onChangeUserManagementHOC(val, "showCreateModal")
          }
        />
      )}
      {props.showEditModal && (
        <UpdateUserForm
          mode={"update"}
          title={getTranslation(outletContext.language, "Update User")}
          language={outletContext.language}
          selectedUser={props.selectedUser}
          updateUser={props.updateUser}
          projectDictionary={outletContext.projectDictionary}
          setOpen={(val) =>
            props.onChangeUserManagementHOC(val, "showEditModal")
          }
        />
      )}
      {props.showResetPasswordModal && (
        <DeleteConfirmation
          isOpen={true}
          onClick={() => props.resetPassword(props.selectedUser.uid)}
          confirmationText={
            <>
              {getTranslation(
                outletContext.language,
                "You are going to reset the password for "
              )}
              <b>({props.selectedUser.display_name})</b>
              {getTranslation(
                outletContext.language,
                " to the default password: "
              )}
              <b>12345678</b>
              <br />
              <br />
              <b className="text-warning">
                {getTranslation(
                  outletContext.language,
                  "Please inform the user about this password change."
                )}
              </b>
            </>
          }
          setOpen={(val) =>
            props.onChangeUserManagementHOC(val, "showResetPasswordModal")
          }
        />
      )}
      {props.showDeleteModal && (
        <DeleteConfirmation
          isOpen={true}
          onClick={() => props.deleteUser(props.selectedUser.uid)}
          confirmationText={
            <>
              {getTranslation(
                outletContext.language,
                "You are going to remove the selected user "
              )}
              <b>({props.selectedUser.display_name})</b>
              {getTranslation(
                outletContext.language,
                " from the system and this will also remove the user from all related project."
              )}
              <br />
              <br />
              <b className="text-danger">
                {getTranslation(
                  outletContext.language,
                  "This action is non-reversible."
                )}
              </b>
            </>
          }
          setOpen={(val) =>
            props.onChangeUserManagementHOC(val, "showDeleteModal")
          }
        />
      )}
      {props.onLoadUserManagement && <LoadingModal />}
    </>
  );
};

export default UserManagementHOC(UserManagement);
